export type SentryBrowserModule = typeof import("@sentry/browser");

let sentryModulePromise: Promise<SentryBrowserModule> | undefined;
let readySentryModule: SentryBrowserModule | undefined;

/** Explicit opt-in only — set VUE_APP_SENTRY_ENABLED=on when you want Sentry. */
export function isSentryClientEnabled(): boolean {
  return process.env.VUE_APP_SENTRY_ENABLED === "on";
}

/** Load monitoring as one named async chunk so it never enters auth startup JS. */
export function loadSentryBrowser(): Promise<SentryBrowserModule> {
  if (!sentryModulePromise) {
    sentryModulePromise = import(
      /* webpackChunkName: "sentry-browser" */ "@sentry/browser"
    );
  }

  return sentryModulePromise;
}

const MAX_QUEUED_SENTRY_TASKS = 50;
let queuedSentryTasks: Array<(Sentry: SentryBrowserModule) => void> = [];

/** Mark Sentry ready only after init has installed its browser integrations. */
export function setSentryBrowserReady(Sentry: SentryBrowserModule): void {
  readySentryModule = Sentry;
  const queued = queuedSentryTasks;
  queuedSentryTasks = [];
  queued.forEach((task) => withSentryBrowser(task));
}

/**
 * Run a task now if Sentry is ready, otherwise once it is. Sentry starts after
 * the first paint, so measurements taken early would otherwise be lost.
 * Never downloads Sentry itself.
 */
export function whenSentryBrowserReady(
  task: (Sentry: SentryBrowserModule) => void
): void {
  if (withSentryBrowser(task) || readySentryModule) return;
  if (queuedSentryTasks.length < MAX_QUEUED_SENTRY_TASKS) {
    queuedSentryTasks.push(task);
  }
}

/**
 * Apply optional context only after Sentry is ready. This avoids accidentally
 * downloading monitoring from a route/tag helper before the deferred bootstrap.
 */
export function withSentryBrowser(
  task: (Sentry: SentryBrowserModule) => void
): boolean {
  if (!readySentryModule) {
    return false;
  }

  try {
    task(readySentryModule);
    return true;
  } catch {
    // Monitoring must never block navigation, authentication, or profile saves.
    return false;
  }
}
